import type { User } from 'oidc-client-ts';
import type { UserRole } from '@/models/UserRole';
import { isNativeCognitoEnabled } from './cognitoFeatureFlag';

export interface OidcPostLoginState {
  returnUrl?: string;
}

const NATIVE_RETURN_URL_KEY = 'allume.native.returnUrl';

/** UserManager intentionally omits user.state from its stored token record. */
export function rememberNativeReturnUrl(user: User, returnUrl?: string): void {
  if (!isNativeCognitoEnabled) return;
  try {
    if (returnUrl && isValidPostLoginReturnUrl(returnUrl)) {
      sessionStorage.setItem(
        NATIVE_RETURN_URL_KEY,
        JSON.stringify({
          subject: user.profile.sub,
          issuer: user.profile.iss,
          clientId: import.meta.env.VITE_IDP_COGNITO_CLIENT_ID,
          returnUrl,
        }),
      );
    } else sessionStorage.removeItem(NATIVE_RETURN_URL_KEY);
  } catch {
    /* Optional navigation metadata; default home remains safe. */
  }
}

export function clearNativeReturnUrl(): void {
  try {
    sessionStorage.removeItem(NATIVE_RETURN_URL_KEY);
  } catch {
    /* Optional navigation metadata. */
  }
}

const BLOCKED_PATH_PREFIXES = [
  '/auth/callback',
  '/auth/error',
  '/auth/onboarding',
  '/login',
  '/commissioning/login',
  '/logout',
];

/**
 * Whether a path is safe to use as an in-app post-login redirect (open-redirect prevention).
 */
export function isValidPostLoginReturnUrl(url: string): boolean {
  if (!url || typeof url !== 'string') {
    return false;
  }
  if (!url.startsWith('/') || url.startsWith('//') || url.includes('\\')) {
    return false;
  }

  const pathOnly = url.split('?')[0]?.split('#')[0] ?? url;
  return !BLOCKED_PATH_PREFIXES.some(
    (blocked) => pathOnly === blocked || pathOnly.startsWith(`${blocked}/`),
  );
}

/**
 * Reads and validates returnUrl from OIDC sign-in state round-tripped via signinRedirect.
 */
export function getReturnUrlFromOidcUser(user: User): string | null {
  let state = user.state;
  if (!state && isNativeCognitoEnabled) {
    try {
      const saved = JSON.parse(sessionStorage.getItem(NATIVE_RETURN_URL_KEY) ?? 'null');
      if (
        saved?.subject === user.profile.sub &&
        saved?.issuer === user.profile.iss &&
        saved?.clientId === import.meta.env.VITE_IDP_COGNITO_CLIENT_ID
      )
        state = saved;
    } catch {
      /* Ignore corrupt or unavailable optional navigation state. */
    }
  }
  if (!state || typeof state !== 'object' || !('returnUrl' in state)) {
    return null;
  }

  const returnUrl = (state as OidcPostLoginState).returnUrl;
  if (typeof returnUrl !== 'string' || !isValidPostLoginReturnUrl(returnUrl)) {
    return null;
  }

  return returnUrl;
}

/** Default home when no return URL was stored in OIDC state. */
function __defaultPostLoginHome(roles: UserRole[] | undefined): string {
  const normalized = roles?.map((role) => role.toLowerCase()) ?? [];
  if (isNativeCognitoEnabled && normalized.includes('consumer')) return '/';
  if (normalized.includes('commissioner')) {
    return '/commissioning/welcome';
  }
  return '/';
}

/** Final navigation target after Cognito callback. */
export function resolvePostLoginRedirect(
  returnUrl: string | null,
  roles: UserRole[] | undefined,
): string {
  if (returnUrl) {
    return returnUrl;
  }
  return __defaultPostLoginHome(roles);
}
